import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { FormattedMessage } from 'react-intl'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LineCalendarCheckIcon from 'LuxKit/Icons/line/LineCalendarCheckIcon'
import LineUserMdIcon from 'LuxKit/Icons/line/LineUserMdIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import type { CSSProperties } from 'react'
import React from 'react'
import styled from 'styled-components'
import eases from 'styles/tools/eases'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useCarouselAutoPlay from 'hooks/useCarouselAutoPlay'
import PaginationDots from 'LuxKit/PaginationDots/PaginationDots'
import { useScreenSizeUp } from 'hooks/useScreenSize'

const SLIDE_COUNT = 3
const SLIDE_DURATION_MS = 3000
const SLIDE_GAP = 16

const CruiseUSPs = styled.div`
  display: grid;
  gap: ${rem(SLIDE_GAP)};
  overflow: visible;
  margin-top: ${rem(24)};

  ${mediaQueryUp.desktop} {
    margin-top: ${rem(36)};
    grid-template-columns: unset;
    grid-auto-flow: column;
    justify-content: space-between;
  }

  &.carousel {
    grid-template-columns: repeat(${SLIDE_COUNT}, 100%);
    transition: transform 0.5s ${eases.power2.inOut};

    ${mediaQueryUp.desktop} {
      grid-template-columns: unset;
    }
  }
`

const CruiseUSP = styled.div`
  display: flex;
  flex-direction: column;

  > * + * {
    margin-top: ${rem(12)};
  }

  ${mediaQueryUp.desktop} {
    width: ${rem(360)};
  }
`

const AnimationContainer = styled(VerticalSpacer)`
  overflow: hidden;
  margin-left: -${rem(16)};
  margin-right: -${rem(16)};
  padding-left: ${rem(16)};
  padding-right: ${rem(16)};

  > ${mediaQueryUp.desktop} {
    margin-left: 0;
    margin-right: 0;
    padding-left: 0;
    padding-right: 0;
  }
`

function CruiseVerticalUSPs() {
  const isCarouselEnabled = !useScreenSizeUp('desktop')
  const { activeIndex, goToSlide, paused, setPaused } = useCarouselAutoPlay(SLIDE_COUNT, SLIDE_DURATION_MS, { enabled: isCarouselEnabled })

  const slideStyle: CSSProperties | undefined = isCarouselEnabled ? {
    transform: `translateX(calc(${-activeIndex} * (100% + ${rem(SLIDE_GAP)})))`,
  } : undefined

  return <AnimationContainer gap={20}>
    <CruiseUSPs className="carousel" style={slideStyle}>
      <CruiseUSP>
        <LineStarIcon size="L" colour="neutral-one" />
        <VerticalSpacer gap={4}>
          <BodyText colour="neutral-one" variant="medium" weight="bold">
            <FormattedMessage
              id="InnZ1RNu5o"
              defaultMessage="Limited-Time Exclusive Offers"
              description="Heading title for exclusive offers USP in cruise booking"
            />
          </BodyText>
          <BodyText variant="medium" colour="neutral-two">
            <FormattedMessage
              id="lhpbVGVcmC"
              defaultMessage="Enjoy the best market offers with added value and exclusive inclusions like specialty dining and onboard credit."
              description="Description for exclusive offers USP in cruise booking"
            />
          </BodyText>
        </VerticalSpacer>
      </CruiseUSP>

      <CruiseUSP>
        <LineCalendarCheckIcon size="L" colour="neutral-one" />
        <VerticalSpacer gap={4}>
          <BodyText colour="neutral-one" variant="medium" weight="bold">
            <FormattedMessage
              id="D2g/2kebwD"
              defaultMessage="Instant Online Bookings"
              description="Heading title for online bookings USP in cruise booking"
            />
          </BodyText>
          <BodyText variant="medium" colour="neutral-two">
            <FormattedMessage
              id="dN1DOSByIm"
              defaultMessage="Browse and book thousands of itineraries and dates with ease and convenience online."
              description="Description for online bookings USP in cruise booking"
            />
          </BodyText>
        </VerticalSpacer>
      </CruiseUSP>

      <CruiseUSP>
        <LineUserMdIcon size="L" colour="neutral-one" />
        <VerticalSpacer gap={4}>
          <BodyText colour="neutral-one" variant="medium" weight="bold">
            <FormattedMessage
              id="Y3vgDHd4Ca"
              defaultMessage="Dedicated Cruise Experts"
              description="Heading title for cruise experts USP in cruise booking"
            />
          </BodyText>
          <BodyText variant="medium" colour="neutral-two">
            <FormattedMessage
              id="9HKpEbgthq"
              defaultMessage="Trust our cruise experts to provide exceptional service and guidance throughout your journey."
              description="Description for cruise experts USP in cruise booking"
            />
          </BodyText>
        </VerticalSpacer>
      </CruiseUSP>
    </CruiseUSPs>

    <CSSBreakpoint max="tablet">
      <PaginationDots
        position="start"
        count={SLIDE_COUNT}
        activeIndex={activeIndex}
        slideDuration={SLIDE_DURATION_MS}
        onSelect={goToSlide}
        paused={paused}
        onHoverChange={setPaused}
      />
    </CSSBreakpoint>
  </AnimationContainer>
}

export default CruiseVerticalUSPs
